import type FullCalendar from '@fullcalendar/react';
import { type RefObject, useCallback, useEffect } from 'react';

/**
 * Enough frames for the rows to become measurable, but few enough that a
 * calendar we can never align stops re-measuring itself.
 */
const MAX_RECOVERY_FRAMES = 20;

/**
 * A resource timeline is two tables side by side: the resource labels on the
 * left, the event lanes on the right. FullCalendar keeps their rows in line by
 * measuring both and writing the taller of each pair back as an inline height.
 *
 * That sync can switch itself off permanently. Before measuring a row
 * FullCalendar marks it unstable, and it only clears that mark once the
 * timeline has coordinates to measure against. It records the marks in a map it
 * never grooms, so a row that unmounts in between leaves an entry behind for
 * good — which happens on the Schedule because the resource list arrives in
 * stages as the users and teams load. From then on every sync pass is skipped
 * unless a resize is *forced*, and both tables fall back to their own natural
 * height: labels a little shorter than lanes holding events. They drift a few
 * pixels further apart on each row until an event lines up against the wrong
 * person entirely.
 *
 * `updateSize` is the only forced pass available to us, so:
 *
 * - Call it whenever the events or resources change, since nothing else will.
 *   A window resize would also do it, but the collapsible panels, animated nav
 *   and container queries that resize this calendar never fire one.
 * - Call it again on a real width change, for the same reason.
 * - Keep calling it until the two tables agree. One pass is not enough — it can
 *   land before the rows are measurable and simply latch again.
 *
 * Upstream issue: https://github.com/fullcalendar/fullcalendar/issues/6082.
 * Do not expect an upgrade to remove the need for this: 6.1.21 ships identical
 * sync logic, and the premium resource-timeline plugin has no stable v7.
 */
export const useKeepResourceRowsAligned = ({
  calendarRef,
  containerRef,
  events,
  resources,
}: {
  calendarRef: RefObject<FullCalendar | null>;
  containerRef: RefObject<HTMLElement | null>;
  /** Only read to detect a change, never inspected. */
  events: unknown;
  /** Only read to detect a change, never inspected. */
  resources: unknown;
}): void => {
  const forceResizeUntilRowsAlign = useCallback(() => {
    const rowHeights = (selector: string) =>
      Array.from(
        containerRef.current?.querySelectorAll<HTMLElement>(selector) ?? []
      ).map((el) => el.style.height);

    const rowsAlign = () => {
      const labels = rowHeights(
        'table.fc-datagrid-body .fc-datagrid-cell-frame'
      );
      const lanes = rowHeights('.fc-timeline-body .fc-timeline-lane-frame');
      // Not a resource timeline, so there are no labels to keep in line.
      if (!labels.length) {
        return true;
      }
      return (
        labels.length === lanes.length &&
        labels.every((height, i) => !!height && height === lanes[i])
      );
    };

    const updateSize = () => calendarRef.current?.getApi().updateSize();
    if (typeof requestAnimationFrame === 'undefined') {
      updateSize();
      return undefined;
    }

    let framesRemaining = MAX_RECOVERY_FRAMES;
    let handle = requestAnimationFrame(function attempt() {
      updateSize();
      framesRemaining -= 1;
      if (framesRemaining > 0 && !rowsAlign()) {
        handle = requestAnimationFrame(attempt);
      }
    });
    return () => cancelAnimationFrame(handle);
  }, [calendarRef, containerRef]);

  useEffect(
    () => forceResizeUntilRowsAlign(),
    [forceResizeUntilRowsAlign, events, resources]
  );

  useEffect(() => {
    const containerEl = containerRef.current;
    if (!containerEl || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    let lastWidth = containerEl.getBoundingClientRect().width;
    let cancelPending: (() => void) | undefined;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      // Width only. Height is an output of sizing, so reacting to it would feed
      // `updateSize` back into itself.
      if (width === undefined || Math.abs(width - lastWidth) < 1) {
        return;
      }
      lastWidth = width;
      cancelPending?.();
      cancelPending = forceResizeUntilRowsAlign();
    });
    observer.observe(containerEl);

    return () => {
      observer.disconnect();
      cancelPending?.();
    };
  }, [containerRef, forceResizeUntilRowsAlign]);
};
